import { DURATION_INPUT_DEFAULT_VALUE, DurationInput } from "@msys/ui";
import { TextField } from "@mui/material";
import React from "react";
import {
  durationInMinutesToTime,
  timeToDurationInMinutes,
} from "../../../utils.js";

interface Props {
  initialMinutes: number;
  // Called once on commit (blur / Enter) with the new duration in minutes.
  onCommit: (estimatedTime: number) => Promise<void> | void;
  // Exits the surrounding InlineEditableCell's edit mode.
  handleBlur: () => void;
}

export function DurationInlineEditor({
  initialMinutes,
  onCommit,
  handleBlur,
}: Props) {
  const initialString =
    typeof initialMinutes === "number"
      ? durationInMinutesToTime(initialMinutes)
      : DURATION_INPUT_DEFAULT_VALUE;
  const [value, setValue] = React.useState<string>(initialString);
  const valueRef = React.useRef(initialString);

  const sealedRef = React.useRef(false);

  const commit = async () => {
    if (sealedRef.current) return;
    sealedRef.current = true;
    const nextMinutes = timeToDurationInMinutes(valueRef.current);
    try {
      if (nextMinutes !== initialMinutes) await onCommit(nextMinutes);
    } catch (error) {
      // Commit failed — unseal and stay in edit mode so the user can retry or
      // Escape-discard. Swallowing here also prevents an unhandled rejection
      // (callers fire-and-forget via `void commit()`).
      sealedRef.current = false;
      console.error("duration commit failed", error);
      return;
    }
    handleBlur();
  };

  const discard = () => {
    if (sealedRef.current) return;
    sealedRef.current = true;
    handleBlur();
  };

  return (
    <DurationInput
      value={value}
      onChange={(_, v) => {
        valueRef.current = v;
        setValue(v);
      }}
      input={
        <TextField
          variant="outlined"
          size="extra-small"
          fullWidth
          autoFocus
          onBlur={() => void commit()}
          onKeyDown={event => {
            if (event.key === "Enter") {
              event.preventDefault();
              (event.target as HTMLInputElement).blur();
            }
            if (event.key === "Escape") {
              event.preventDefault();
              event.stopPropagation();
              discard();
            }
          }}
        />
      }
    />
  );
}
